import React, { useState, useEffect, useCallback } from 'react';
import { Plus, ChevronLeft, Edit2, Trash2, Tag, X, Info, TrendingUp } from 'lucide-react';
import apiService from '../../services/ApiService';
import { useApp } from '../../context/AppContext';
import { isManagerOrAbove } from '../../utils/roleHelpers';
import ShipmentModal from './ShipmentModal';
import ShipmentItemModal from './ShipmentItemModal';

// ── Design tokens ──────────────────────────────────────────────────────────────
const FONT      = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif';
const BG        = '#F2F2F7';
const CARD      = { background: '#fff', borderRadius: 18, boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 20px rgba(0,0,0,0.07)' };
const INK       = '#1C1C1E';
const MUTED     = '#8E8E93';
const SECONDARY = '#3C3C43';
const SYS_GREEN  = '#34C759';
const SYS_RED    = '#FF3B30';
const SYS_BLUE   = '#007AFF';
const SYS_ORANGE = '#FF9500';
const BRAND      = '#84CC16';
const INP = { width: '100%', padding: '10px 13px', border: 'none', borderRadius: 10, fontSize: 14, outline: 'none', background: '#F2F2F7', color: INK, fontFamily: FONT, boxSizing: 'border-box' };
const LBL = { display: 'block', fontSize: 11, fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6, fontFamily: FONT };

// ── Formatters ─────────────────────────────────────────────────────────────────
function fmt(n)  { return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function pct(n)  { return (Number(n || 0) * 100).toFixed(1) + '%'; }

// ── Calculation engine ─────────────────────────────────────────────────────────
const COST_KEYS = ['shipping','importFees','customsDuties','brokerFees','portFees','trucking','insurance','warehouseReceiving','other'];
const COST_LABELS = {
  shipping:'Shipping', importFees:'Import Fees', customsDuties:'Customs / Duties',
  brokerFees:'Broker Fees', portFees:'Port Fees', trucking:'Trucking',
  insurance:'Insurance', warehouseReceiving:'Warehouse Receiving', other:'Other',
};

function calcTotalShared(costs) {
  return COST_KEYS.reduce((s, k) => s + (parseFloat(costs?.[k]) || 0), 0);
}

function calcShare(item, items, method) {
  switch (method) {
    case 'quantity': {
      const t = items.reduce((s, i) => s + (i.quantity||0), 0);
      return t > 0 ? (item.quantity||0) / t : 0;
    }
    case 'weight': {
      const t = items.reduce((s, i) => s + (i.weight||0)*(i.quantity||0), 0);
      return t > 0 ? ((item.weight||0)*(item.quantity||0)) / t : 0;
    }
    case 'volume': {
      const t = items.reduce((s, i) => s + (i.volume||0)*(i.quantity||0), 0);
      return t > 0 ? ((item.volume||0)*(item.quantity||0)) / t : 0;
    }
    case 'manual':
      return Math.min(1, (item.manualAllocationPct||0) / 100);
    default: {
      const t = items.reduce((s, i) => s + (i.quantity||0)*(i.unitCost||0), 0);
      return t > 0 ? ((item.quantity||0)*(item.unitCost||0)) / t : 0;
    }
  }
}

function calcLanded(item, shipment, items) {
  const totalShared = calcTotalShared(shipment.costs);
  const share = calcShare(item, items, shipment.allocationMethod || 'value');
  const allocTotal = share * totalShared;
  const qty = item.quantity || 1;
  const allocPerUnit = allocTotal / qty;
  const breakdown = {};
  COST_KEYS.forEach(k => { breakdown[k] = share * (parseFloat(shipment.costs?.[k]) || 0) / qty; });
  return { share, allocTotal, allocPerUnit, breakdown, landedCost: (item.unitCost||0) + allocPerUnit };
}

function calcPricing(landed, scenario, monthlyOverhead) {
  const sc = scenario || {};
  const storageCost = (sc.storage?.monthlyCostPerUnit||0) * (sc.storage?.months||0);
  const expectedSales = Math.max(1, sc.overhead?.expectedMonthlySales || 1);
  const ovhPerUnit = monthlyOverhead / expectedSales;
  const fees = sc.sellingFees || [];
  const fixedFees = fees.filter(f=>f.type==='fixed').reduce((s,f)=>s+(parseFloat(f.value)||0),0);
  const pctFees  = fees.filter(f=>f.type==='percent').reduce((s,f)=>s+(parseFloat(f.value)||0),0) / 100;
  const trueCost = landed.landedCost + storageCost + ovhPerUnit + fixedFees;
  const margin   = sc.targetMargin ?? 0.30;
  const denom    = 1 - margin - pctFees;
  const recPrice = denom > 0.01 ? trueCost / denom : trueCost * (1 + margin);
  const bePrice  = (1 - pctFees) > 0 ? trueCost / (1 - pctFees) : trueCost;
  const profitPU = recPrice*(1 - pctFees) - trueCost;
  const actualMargin = recPrice > 0 ? profitPU / recPrice : 0;
  const markup   = trueCost > 0 ? (profitPU/trueCost)*100 : 0;
  const curPrice = sc.currentSellingPrice || 0;
  const curProfit= curPrice > 0 ? curPrice*(1-pctFees) - trueCost : null;
  const curMargin= curPrice > 0 ? curProfit/curPrice : null;
  const unitsOvh = profitPU > 0 ? Math.ceil(monthlyOverhead/profitPU) : null;
  return { storageCost, ovhPerUnit, fixedFees, pctFees, trueCost, margin, recPrice, bePrice,
    profitPU, actualMargin, markup, curPrice, curProfit, curMargin, unitsOvh, monthlyOverhead };
}

function getStatus(p) {
  if (!p) return { label:'No Scenario', color:MUTED, bg:'#F2F2F7' };
  if (p.curPrice > 0 && p.curPrice < p.trueCost) return { label:'Below Cost', color:SYS_RED, bg:'rgba(255,59,48,0.1)' };
  if (p.actualMargin >= 0.30) return { label:'Healthy', color:SYS_GREEN, bg:'rgba(52,199,89,0.1)' };
  if (p.actualMargin >= 0.15) return { label:'Low Margin', color:SYS_ORANGE, bg:'rgba(255,149,0,0.1)' };
  if (p.actualMargin > 0)     return { label:'Needs Review', color:SYS_BLUE, bg:'rgba(0,122,255,0.1)' };
  return { label:'No Scenario', color:MUTED, bg:'#F2F2F7' };
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function LandedCostPage() {
  const { currentUser } = useApp();
  const [tab, setTab]   = useState('overview');
  const [shipments, setShipments]   = useState([]);
  const [allItems, setAllItems]     = useState([]);
  const [allScenarios, setAllScenarios] = useState([]);
  const [recurring, setRecurring]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  const [selectedShipment, setSelectedShipment] = useState(null);
  const [shipmentModal,    setShipmentModal]     = useState(null);
  const [itemModal,        setItemModal]         = useState(null);
  const [pricingItem,      setPricingItem]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, i, sc, rec] = await Promise.all([
        apiService.getShipments(),
        apiService.getShipmentItems(),
        apiService.getPricingScenarios(),
        apiService.getRecurring(),
      ]);
      setShipments(s); setAllItems(i); setAllScenarios(sc); setRecurring(rec);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!isManagerOrAbove(currentUser)) {
    return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', fontFamily:FONT, color:MUTED }}>Access restricted to Managers and Admins.</div>;
  }

  const monthlyOverhead = recurring.filter(r => r.active !== false)
    .reduce((s, r) => s + (r.frequency==='monthly' ? r.amount : r.amount/12), 0);

  // Shipment CRUD
  async function handleSaveShipment(form) {
    if (shipmentModal === 'new') {
      const created = await apiService.createShipment(form);
      setShipments(prev => [...prev, created]);
    } else {
      const updated = await apiService.updateShipment(form.id, form);
      setShipments(prev => prev.map(s => s.id === updated.id ? updated : s));
      if (selectedShipment?.id === updated.id) setSelectedShipment(updated);
    }
  }
  async function handleDeleteShipment(s) {
    if (!window.confirm(`Delete shipment "${s.name}"? This cannot be undone.`)) return;
    await apiService.deleteShipment(s.id);
    setShipments(prev => prev.filter(x => x.id !== s.id));
    setAllItems(prev => prev.filter(i => i.shipmentId !== s.id));
    setAllScenarios(prev => prev.filter(sc => !allItems.filter(i => i.shipmentId === s.id).map(i => i.id).includes(sc.itemId)));
    if (selectedShipment?.id === s.id) setSelectedShipment(null);
  }

  // Item CRUD
  async function handleSaveItem(form) {
    if (itemModal === 'new') {
      const created = await apiService.createShipmentItem(form);
      setAllItems(prev => [...prev, created]);
    } else {
      const updated = await apiService.updateShipmentItem(form.id, form);
      setAllItems(prev => prev.map(i => i.id === updated.id ? updated : i));
    }
  }
  async function handleDeleteItem(item) {
    if (!window.confirm(`Remove "${item.productName}"?`)) return;
    await apiService.deleteShipmentItem(item.id);
    setAllItems(prev => prev.filter(i => i.id !== item.id));
    setAllScenarios(prev => prev.filter(s => s.itemId !== item.id));
  }

  // Pricing scenario save
  async function handleSaveScenario(form) {
    const existing = allScenarios.find(s => s.itemId === form.itemId);
    if (existing) {
      const updated = await apiService.updatePricingScenario(existing.id, form);
      setAllScenarios(prev => prev.map(s => s.id === updated.id ? updated : s));
    } else {
      const created = await apiService.createPricingScenario(form);
      setAllScenarios(prev => [...prev, created]);
    }
  }

  const shipmentItems = selectedShipment ? allItems.filter(i => i.shipmentId === selectedShipment.id) : [];

  if (loading) return <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', background:BG, fontFamily:FONT, color:MUTED }}>Loading…</div>;

  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column', overflow:'hidden', background:BG, fontFamily:FONT }}>
      {error && <div style={{ background:'#FFF1F0', color:SYS_RED, padding:'10px 20px', fontSize:13 }}>{error}</div>}

      {/* Tab bar */}
      <div style={{ background:'#fff', borderBottom:'1px solid rgba(0,0,0,0.07)', padding:'12px 20px', flexShrink:0, display:'flex', alignItems:'center', gap:12 }}>
        {selectedShipment && tab === 'shipments' && (
          <button onClick={() => setSelectedShipment(null)} style={{ background:'none', border:'none', cursor:'pointer', color:SYS_BLUE, display:'flex', alignItems:'center', gap:4, fontSize:14, fontFamily:FONT, padding:0, marginRight:4 }}>
            <ChevronLeft size={16} /> Shipments
          </button>
        )}
        <div style={{ display:'inline-flex', gap:0, background:'#E5E5EA', borderRadius:11, padding:3 }}>
          {[['overview','Overview'],['shipments', selectedShipment ? selectedShipment.name : 'Shipments'],['simulator','Simulator']].map(([v, l]) => (
            <button key={v} onClick={() => { setTab(v); if (v !== 'shipments') setSelectedShipment(null); }} style={{
              padding:'7px 16px', borderRadius:9, border:'none', whiteSpace:'nowrap', cursor:'pointer', fontSize:13, fontFamily:FONT,
              background: tab===v ? '#fff' : 'transparent',
              boxShadow: tab===v ? '0 1px 4px rgba(0,0,0,0.15)' : 'none',
              color: tab===v ? INK : MUTED, fontWeight: tab===v ? 600 : 400,
              transition:'all 0.15s ease', maxWidth: v==='shipments' && selectedShipment ? 180 : undefined,
              overflow:'hidden', textOverflow:'ellipsis',
            }}>{l}</button>
          ))}
        </div>
      </div>

      <div style={{ flex:1, overflowY:'auto', padding:20 }}>

        {/* ══ OVERVIEW ══════════════════════════════════════════════════════════ */}
        {tab === 'overview' && (
          <OverviewTab allItems={allItems} allScenarios={allScenarios} shipments={shipments} monthlyOverhead={monthlyOverhead} onOpenPricing={item => { setPricingItem(item); }} />
        )}

        {/* ══ SHIPMENTS ═════════════════════════════════════════════════════════ */}
        {tab === 'shipments' && !selectedShipment && (
          <ShipmentListTab shipments={shipments} allItems={allItems}
            onSelect={s => setSelectedShipment(s)}
            onNew={() => setShipmentModal('new')}
            onEdit={s => setShipmentModal(s)}
            onDelete={handleDeleteShipment}
          />
        )}

        {tab === 'shipments' && selectedShipment && (
          <ShipmentDetailTab
            shipment={selectedShipment} items={shipmentItems} allScenarios={allScenarios} monthlyOverhead={monthlyOverhead}
            onEditShipment={() => setShipmentModal(selectedShipment)}
            onAddItem={() => setItemModal('new')}
            onEditItem={item => setItemModal(item)}
            onDeleteItem={handleDeleteItem}
            onOpenPricing={item => setPricingItem(item)}
          />
        )}

        {/* ══ SIMULATOR ════════════════════════════════════════════════════════ */}
        {tab === 'simulator' && (
          <SimulatorTab monthlyOverhead={monthlyOverhead} />
        )}
      </div>

      {/* ── Modals ── */}
      {shipmentModal && (
        <ShipmentModal shipment={shipmentModal === 'new' ? null : shipmentModal} onSave={handleSaveShipment} onClose={() => setShipmentModal(null)} />
      )}
      {itemModal && selectedShipment && (
        <ShipmentItemModal item={itemModal === 'new' ? null : itemModal} shipmentId={selectedShipment.id} allocationMethod={selectedShipment.allocationMethod} onSave={handleSaveItem} onClose={() => setItemModal(null)} />
      )}
      {pricingItem && (
        <PricingPanel
          item={pricingItem}
          shipment={shipments.find(s => s.id === pricingItem.shipmentId)}
          items={allItems.filter(i => i.shipmentId === pricingItem.shipmentId)}
          scenario={allScenarios.find(s => s.itemId === pricingItem.id) || null}
          monthlyOverhead={monthlyOverhead}
          onSave={handleSaveScenario}
          onClose={() => setPricingItem(null)}
        />
      )}
    </div>
  );
}

// ── Overview tab ───────────────────────────────────────────────────────────────
function OverviewTab({ allItems, allScenarios, shipments, monthlyOverhead, onOpenPricing }) {
  const rows = allItems.map(item => {
    const shipment = shipments.find(s => s.id === item.shipmentId);
    if (!shipment) return null;
    const items = allItems.filter(i => i.shipmentId === item.shipmentId);
    const landed = calcLanded(item, shipment, items);
    const scenario = allScenarios.find(s => s.itemId === item.id) || null;
    const pricing  = scenario ? calcPricing(landed, scenario, monthlyOverhead) : null;
    const status   = getStatus(pricing);
    return { item, shipment, landed, pricing, status, scenario };
  }).filter(Boolean);

  const totalItems = rows.length;
  const healthy   = rows.filter(r => r.status.label === 'Healthy').length;
  const belowCost = rows.filter(r => r.status.label === 'Below Cost').length;
  const noScenario= rows.filter(r => !r.scenario).length;

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:14, marginBottom:20 }}>
        {[
          { label:'Total Products', val:totalItems, color:INK },
          { label:'Healthy Margin', val:healthy, color:SYS_GREEN },
          { label:'Below Cost', val:belowCost, color:SYS_RED },
          { label:'Needs Pricing', val:noScenario, color:SYS_ORANGE },
        ].map(({ label, val, color }) => (
          <div key={label} style={{ ...CARD, padding:'18px 20px' }}>
            <div style={{ fontSize:11, fontWeight:600, color:MUTED, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6 }}>{label}</div>
            <div style={{ fontSize:28, fontWeight:700, color, fontVariantNumeric:'tabular-nums' }}>{val}</div>
          </div>
        ))}
      </div>

      <div style={{ ...CARD, overflow:'hidden' }}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid #F2F2F7' }}>
          <h3 style={{ fontSize:15, fontWeight:700, color:INK, margin:0 }}>All Products — Landed Cost & Pricing</h3>
        </div>
        {rows.length === 0 ? (
          <div style={{ padding:48, textAlign:'center', color:MUTED }}>
            <Tag size={28} color="#E5E5EA" style={{ marginBottom:10 }} />
            <div style={{ fontSize:14 }}>No products yet.</div>
            <div style={{ fontSize:12, marginTop:4 }}>Create a shipment and add products to get started.</div>
          </div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ background:BG }}>
                  {['Product','SKU','Supplier Cost','Landed Cost','True Cost','Rec. Price','Margin','Status',''].map(h => (
                    <th key={h} style={{ textAlign:'left', padding:'10px 14px', color:MUTED, fontWeight:600, fontSize:10, textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(({ item, landed, pricing, status, scenario }) => (
                  <tr key={item.id} style={{ borderBottom:'1px solid #F9F9F9' }}>
                    <td style={{ padding:'11px 14px', fontWeight:600, color:INK, maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.productName}</td>
                    <td style={{ padding:'11px 14px', color:MUTED }}>{item.sku || '—'}</td>
                    <td style={{ padding:'11px 14px', fontVariantNumeric:'tabular-nums', color:SECONDARY }}>{fmt(item.unitCost)}</td>
                    <td style={{ padding:'11px 14px', fontVariantNumeric:'tabular-nums', color:SYS_BLUE, fontWeight:600 }}>{fmt(landed.landedCost)}</td>
                    <td style={{ padding:'11px 14px', fontVariantNumeric:'tabular-nums', color:pricing ? INK : MUTED, fontWeight:pricing ? 700 : 400 }}>{pricing ? fmt(pricing.trueCost) : '—'}</td>
                    <td style={{ padding:'11px 14px', fontVariantNumeric:'tabular-nums', color:BRAND, fontWeight:700 }}>{pricing ? fmt(pricing.recPrice) : '—'}</td>
                    <td style={{ padding:'11px 14px', color:pricing ? (pricing.actualMargin >= 0.25 ? SYS_GREEN : SYS_ORANGE) : MUTED }}>
                      {pricing ? pct(pricing.actualMargin) : '—'}
                    </td>
                    <td style={{ padding:'11px 14px' }}>
                      <span style={{ padding:'3px 9px', borderRadius:20, fontSize:10, fontWeight:600, color:status.color, background:status.bg, whiteSpace:'nowrap' }}>{status.label}</span>
                    </td>
                    <td style={{ padding:'11px 14px' }}>
                      <button onClick={() => onOpenPricing(item)} style={{ padding:'5px 12px', border:'none', borderRadius:8, background:BRAND+'18', color:BRAND, cursor:'pointer', fontSize:11, fontWeight:600, fontFamily:FONT, whiteSpace:'nowrap' }}>
                        {scenario ? 'Edit Pricing' : 'Set Pricing'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Shipment list tab ──────────────────────────────────────────────────────────
function ShipmentListTab({ shipments, allItems, onSelect, onNew, onEdit, onDelete }) {
  const STATUS_COLOR = { draft:MUTED, received:SYS_BLUE, costed:SYS_GREEN };
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
        <button onClick={onNew} style={{ padding:'10px 20px', background:BRAND, border:'none', borderRadius:12, color:'white', fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontSize:14, fontFamily:FONT }}>
          <Plus size={15} /> New Shipment
        </button>
      </div>
      {shipments.length === 0 ? (
        <div style={{ ...CARD, padding:48, textAlign:'center', color:MUTED }}>
          <div style={{ fontSize:14 }}>No shipments yet.</div>
          <div style={{ fontSize:12, marginTop:4 }}>Create your first container/shipment to start calculating landed costs.</div>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:16 }}>
          {shipments.map(s => {
            const items = allItems.filter(i => i.shipmentId === s.id);
            const totalShared = calcTotalShared(s.costs);
            const totalSupplier = items.reduce((sum, i) => sum + (i.quantity||0)*(i.unitCost||0), 0);
            const sc = STATUS_COLOR[s.status] || MUTED;
            return (
              <div key={s.id} style={{ ...CARD, padding:'20px 22px', cursor:'pointer' }} onClick={() => onSelect(s)}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:15, fontWeight:700, color:INK, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.name}</div>
                    <div style={{ fontSize:12, color:MUTED, marginTop:2 }}>{s.supplier || 'No supplier'}{s.origin ? ` · ${s.origin}` : ''}</div>
                  </div>
                  <span style={{ padding:'3px 10px', borderRadius:20, fontSize:10, fontWeight:700, color:sc, background:sc+'18', flexShrink:0, marginLeft:8, textTransform:'capitalize' }}>{s.status}</span>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:14 }}>
                  <div style={{ background:BG, borderRadius:10, padding:'10px 12px' }}>
                    <div style={{ fontSize:10, color:MUTED, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>Products</div>
                    <div style={{ fontSize:18, fontWeight:700, color:INK, marginTop:2 }}>{items.length}</div>
                  </div>
                  <div style={{ background:BG, borderRadius:10, padding:'10px 12px' }}>
                    <div style={{ fontSize:10, color:MUTED, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>Supplier $</div>
                    <div style={{ fontSize:13, fontWeight:700, color:SYS_BLUE, marginTop:2, fontVariantNumeric:'tabular-nums' }}>{fmt(totalSupplier)}</div>
                  </div>
                  <div style={{ background:BG, borderRadius:10, padding:'10px 12px' }}>
                    <div style={{ fontSize:10, color:MUTED, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>Shared $</div>
                    <div style={{ fontSize:13, fontWeight:700, color:SYS_ORANGE, marginTop:2, fontVariantNumeric:'tabular-nums' }}>{fmt(totalShared)}</div>
                  </div>
                </div>
                {s.arrivalDate && <div style={{ fontSize:11, color:MUTED, marginBottom:10 }}>Arrived: {s.arrivalDate}</div>}
                <div style={{ display:'flex', gap:8 }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => onEdit(s)} style={{ flex:1, padding:'7px', border:'1px solid #E5E5EA', borderRadius:9, background:'white', cursor:'pointer', color:SECONDARY, fontSize:12, fontFamily:FONT, display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}><Edit2 size={11} /> Edit</button>
                  <button onClick={() => onDelete(s)} style={{ padding:'7px 12px', border:'1px solid #FFD0CC', borderRadius:9, background:'white', cursor:'pointer', color:SYS_RED, fontSize:12, fontFamily:FONT }}><Trash2 size={11} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Shipment detail tab ────────────────────────────────────────────────────────
function ShipmentDetailTab({ shipment, items, allScenarios, monthlyOverhead, onEditShipment, onAddItem, onEditItem, onDeleteItem, onOpenPricing }) {
  const totalShared   = calcTotalShared(shipment.costs);
  const totalSupplier = items.reduce((s, i) => s + (i.quantity||0)*(i.unitCost||0), 0);
  const ALLOC_LABELS  = { value:'By Value', quantity:'By Qty', weight:'By Weight', volume:'By Volume', manual:'Manual' };

  return (
    <div>
      {/* Shipment header */}
      <div style={{ ...CARD, padding:'20px 24px', marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
          <div>
            <h2 style={{ fontSize:20, fontWeight:700, color:INK, margin:'0 0 4px' }}>{shipment.name}</h2>
            <div style={{ fontSize:13, color:MUTED }}>{shipment.supplier || 'No supplier'}{shipment.origin ? ` · from ${shipment.origin}` : ''}{shipment.containerNumber ? ` · ${shipment.containerNumber}` : ''}</div>
          </div>
          <button onClick={onEditShipment} style={{ padding:'8px 16px', border:'1px solid #E5E5EA', borderRadius:10, background:'white', cursor:'pointer', fontSize:13, fontWeight:500, color:SECONDARY, display:'flex', alignItems:'center', gap:6, fontFamily:FONT, flexShrink:0 }}>
            <Edit2 size={12} /> Edit Shipment
          </button>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:12, marginTop:16 }}>
          <div style={{ background:BG, borderRadius:12, padding:'12px 16px' }}>
            <div style={{ fontSize:10, color:MUTED, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>Allocation</div>
            <div style={{ fontSize:14, fontWeight:600, color:INK }}>{ALLOC_LABELS[shipment.allocationMethod] || 'By Value'}</div>
          </div>
          <div style={{ background:BG, borderRadius:12, padding:'12px 16px' }}>
            <div style={{ fontSize:10, color:MUTED, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>Total Shared</div>
            <div style={{ fontSize:14, fontWeight:700, color:SYS_ORANGE, fontVariantNumeric:'tabular-nums' }}>{fmt(totalShared)}</div>
          </div>
          <div style={{ background:BG, borderRadius:12, padding:'12px 16px' }}>
            <div style={{ fontSize:10, color:MUTED, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>Supplier Total</div>
            <div style={{ fontSize:14, fontWeight:700, color:SYS_BLUE, fontVariantNumeric:'tabular-nums' }}>{fmt(totalSupplier)}</div>
          </div>
          <div style={{ background:BG, borderRadius:12, padding:'12px 16px' }}>
            <div style={{ fontSize:10, color:MUTED, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>Grand Total</div>
            <div style={{ fontSize:14, fontWeight:700, color:BRAND, fontVariantNumeric:'tabular-nums' }}>{fmt(totalShared + totalSupplier)}</div>
          </div>
        </div>

        {/* Shared cost breakdown */}
        {totalShared > 0 && (
          <div style={{ marginTop:14, paddingTop:14, borderTop:'1px solid #F2F2F7' }}>
            <div style={{ fontSize:11, fontWeight:600, color:MUTED, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>Cost Breakdown</div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {COST_KEYS.filter(k => (shipment.costs?.[k]||0) > 0).map(k => (
                <div key={k} style={{ background:SYS_ORANGE+'12', borderRadius:8, padding:'4px 12px', fontSize:12 }}>
                  <span style={{ color:MUTED }}>{COST_LABELS[k]}: </span>
                  <span style={{ fontWeight:700, color:SYS_ORANGE }}>{fmt(shipment.costs[k])}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Products */}
      <div style={{ ...CARD, overflow:'hidden' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px', borderBottom:'1px solid #F2F2F7' }}>
          <h3 style={{ fontSize:15, fontWeight:700, color:INK, margin:0 }}>Products ({items.length})</h3>
          <button onClick={onAddItem} style={{ padding:'8px 16px', background:BRAND, border:'none', borderRadius:10, color:'white', fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontSize:13, fontFamily:FONT }}>
            <Plus size={13} /> Add Product
          </button>
        </div>

        {items.length === 0 ? (
          <div style={{ padding:48, textAlign:'center', color:MUTED, fontSize:14 }}>No products in this shipment yet. Add products to see landed costs.</div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ background:BG }}>
                  {['Product','Qty','Unit Cost','Share %','Allocated/Unit','Landed Cost/Unit','Scenario',''].map(h => (
                    <th key={h} style={{ textAlign:'left', padding:'9px 14px', color:MUTED, fontWeight:600, fontSize:10, textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const landed   = calcLanded(item, shipment, items);
                  const scenario = allScenarios.find(s => s.itemId === item.id);
                  const pricing  = scenario ? calcPricing(landed, scenario, monthlyOverhead) : null;
                  return (
                    <tr key={item.id} style={{ borderBottom:'1px solid #F9F9F9' }}>
                      <td style={{ padding:'11px 14px' }}>
                        <div style={{ fontWeight:600, color:INK }}>{item.productName}</div>
                        {item.sku && <div style={{ fontSize:10, color:MUTED }}>{item.sku}</div>}
                      </td>
                      <td style={{ padding:'11px 14px', color:SECONDARY }}>{(item.quantity||0).toLocaleString()}</td>
                      <td style={{ padding:'11px 14px', color:SECONDARY, fontVariantNumeric:'tabular-nums' }}>{fmt(item.unitCost)}</td>
                      <td style={{ padding:'11px 14px', color:MUTED }}>{(landed.share * 100).toFixed(1)}%</td>
                      <td style={{ padding:'11px 14px', color:SYS_ORANGE, fontWeight:600, fontVariantNumeric:'tabular-nums' }}>{fmt(landed.allocPerUnit)}</td>
                      <td style={{ padding:'11px 14px', color:SYS_BLUE, fontWeight:700, fontVariantNumeric:'tabular-nums' }}>{fmt(landed.landedCost)}</td>
                      <td style={{ padding:'11px 14px' }}>
                        {pricing ? (
                          <div>
                            <div style={{ fontWeight:600, color:BRAND, fontVariantNumeric:'tabular-nums' }}>{fmt(pricing.recPrice)}</div>
                            <div style={{ fontSize:10, color:MUTED }}>{pct(pricing.actualMargin)} margin</div>
                          </div>
                        ) : <span style={{ color:MUTED, fontSize:11 }}>No scenario</span>}
                      </td>
                      <td style={{ padding:'11px 14px' }}>
                        <div style={{ display:'flex', gap:6 }}>
                          <button onClick={() => onOpenPricing(item)} style={{ padding:'5px 10px', border:'none', borderRadius:8, background:BRAND+'18', color:BRAND, cursor:'pointer', fontSize:11, fontWeight:600, fontFamily:FONT, whiteSpace:'nowrap' }}>
                            {scenario ? 'Pricing' : 'Set Price'}
                          </button>
                          <button onClick={() => onEditItem(item)} style={{ padding:'5px 8px', border:'1px solid #E5E5EA', borderRadius:8, background:'white', cursor:'pointer', color:SECONDARY }}><Edit2 size={10} /></button>
                          <button onClick={() => onDeleteItem(item)} style={{ padding:'5px 8px', border:'1px solid #FFD0CC', borderRadius:8, background:'white', cursor:'pointer', color:SYS_RED }}><Trash2 size={10} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Simulator tab ──────────────────────────────────────────────────────────────
function SimulatorTab({ monthlyOverhead }) {
  const [sim, setSim] = useState({
    unitCost: 5, shippingPerUnit: 2, otherAllocPerUnit: 1,
    storageMonthlyCostPerUnit: 0.5, storageMonths: 0,
    expectedMonthlySales: 100, targetMargin: 30,
    feePercent: 3, feeFixed: 0, currentPrice: 0,
  });
  function set(k, v) { setSim(s => ({ ...s, [k]: parseFloat(v) || 0 })); }

  const landedCost  = sim.unitCost + sim.shippingPerUnit + sim.otherAllocPerUnit;
  const storageCost = sim.storageMonthlyCostPerUnit * sim.storageMonths;
  const ovhPerUnit  = monthlyOverhead / Math.max(1, sim.expectedMonthlySales);
  const feesPct     = sim.feePercent / 100;
  const trueCost    = landedCost + storageCost + ovhPerUnit + sim.feeFixed;
  const margin      = sim.targetMargin / 100;
  const denom       = 1 - margin - feesPct;
  const recPrice    = denom > 0.01 ? trueCost / denom : trueCost * (1 + margin);
  const bePrice     = (1 - feesPct) > 0 ? trueCost / (1 - feesPct) : trueCost;
  const profitPU    = recPrice*(1 - feesPct) - trueCost;
  const curProfit   = sim.currentPrice > 0 ? sim.currentPrice*(1-feesPct) - trueCost : null;
  const curMargin   = sim.currentPrice > 0 ? curProfit/sim.currentPrice : null;

  const R = ({ label, val, color, bold }) => (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid #F9F9F9' }}>
      <span style={{ fontSize:13, color:SECONDARY }}>{label}</span>
      <span style={{ fontSize: bold ? 16 : 14, fontWeight: bold ? 700 : 500, color: color||INK, fontVariantNumeric:'tabular-nums' }}>{val}</span>
    </div>
  );

  const Sl = ({ label, field, min, max, step, suffix='', prefix='' }) => (
    <div style={{ marginBottom:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
        <label style={{ fontSize:12, fontWeight:600, color:MUTED, textTransform:'uppercase', letterSpacing:'0.06em' }}>{label}</label>
        <span style={{ fontSize:13, fontWeight:700, color:INK }}>{prefix}{sim[field]}{suffix}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={sim[field]} onChange={e => set(field, e.target.value)}
        style={{ width:'100%', accentColor:BRAND }} />
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:MUTED, marginTop:2 }}>
        <span>{prefix}{min}{suffix}</span><span>{prefix}{max}{suffix}</span>
      </div>
    </div>
  );

  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap:20, alignItems:'start' }}>
      {/* Inputs */}
      <div style={{ ...CARD, padding:'22px 24px' }}>
        <h3 style={{ fontSize:15, fontWeight:700, color:INK, margin:'0 0 18px', display:'flex', alignItems:'center', gap:8 }}>
          <TrendingUp size={15} color={BRAND} /> Simulator Inputs
        </h3>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:18 }}>
          {[
            ['Supplier Unit Cost ($)', 'unitCost'],
            ['Shipping Allocated/Unit ($)', 'shippingPerUnit'],
            ['Other Allocated/Unit ($)', 'otherAllocPerUnit'],
            ['Current Selling Price ($)', 'currentPrice'],
          ].map(([label, field]) => (
            <div key={field}>
              <label style={LBL}>{label}</label>
              <input type="number" min="0" step="0.01" value={sim[field]} onChange={e => set(field, e.target.value)} style={INP} />
            </div>
          ))}
        </div>
        <Sl label="Target Margin" field="targetMargin" min={5} max={70} step={1} suffix="%" />
        <Sl label="Selling Fee %" field="feePercent" min={0} max={30} step={0.5} suffix="%" />
        <Sl label="Storage (months)" field="storageMonths" min={0} max={24} step={1} suffix=" mo" />
        <Sl label="Storage Cost/Unit/Month ($)" field="storageMonthlyCostPerUnit" min={0} max={5} step={0.1} prefix="$" />
        <Sl label="Expected Monthly Sales (units)" field="expectedMonthlySales" min={1} max={1000} step={1} suffix=" units" />
        {sim.feeFixed > 0 || true ? (
          <div style={{ marginBottom:8 }}>
            <label style={LBL}>Fixed Fees / Unit ($)</label>
            <input type="number" min="0" step="0.01" value={sim.feeFixed} onChange={e => set('feeFixed', e.target.value)} style={INP} />
          </div>
        ) : null}
        <div style={{ marginTop:12, padding:'10px 14px', borderRadius:10, background:'rgba(132,204,22,0.08)', fontSize:11, color:MUTED }}>
          <Info size={11} style={{ verticalAlign:'middle', marginRight:4 }} />
          Monthly overhead ({fmt(monthlyOverhead)}/mo) is auto-pulled from your recurring expenses.
        </div>
      </div>

      {/* Results */}
      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
        <div style={{ ...CARD, padding:'22px 24px' }}>
          <h3 style={{ fontSize:15, fontWeight:700, color:INK, margin:'0 0 14px' }}>Cost Breakdown</h3>
          <R label="Supplier Unit Cost" val={fmt(sim.unitCost)} />
          <R label="Shipping (allocated/unit)" val={fmt(sim.shippingPerUnit)} />
          <R label="Other allocated/unit" val={fmt(sim.otherAllocPerUnit)} />
          <R label="Landed Cost" val={fmt(landedCost)} color={SYS_BLUE} bold />
          <R label={`Storage (${sim.storageMonths} mo × $${sim.storageMonthlyCostPerUnit})`} val={fmt(storageCost)} />
          <R label={`Overhead (${fmt(monthlyOverhead)}/mo ÷ ${sim.expectedMonthlySales} units)`} val={fmt(ovhPerUnit)} />
          <R label="Fixed Fees/unit" val={fmt(sim.feeFixed)} />
          <R label="True Cost / Unit" val={fmt(trueCost)} color={INK} bold />
        </div>

        <div style={{ ...CARD, padding:'22px 24px' }}>
          <h3 style={{ fontSize:15, fontWeight:700, color:INK, margin:'0 0 14px' }}>Pricing Output</h3>
          <div style={{ background:BRAND+'12', borderRadius:12, padding:'14px 16px', marginBottom:14 }}>
            <div style={{ fontSize:11, fontWeight:600, color:MUTED, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:4 }}>Recommended Price ({sim.targetMargin}% margin)</div>
            <div style={{ fontSize:32, fontWeight:800, color:BRAND, fontVariantNumeric:'tabular-nums' }}>{fmt(recPrice)}</div>
            <div style={{ fontSize:12, color:MUTED, marginTop:2 }}>= {fmt(trueCost)} / (1 − {sim.targetMargin}% − {sim.feePercent}%)</div>
          </div>
          <R label="Break-Even Price" val={fmt(bePrice)} color={SYS_ORANGE} />
          <R label="Profit / Unit" val={fmt(profitPU)} color={profitPU >= 0 ? SYS_GREEN : SYS_RED} bold />
          <R label="Actual Margin" val={pct(profitPU/recPrice)} color={profitPU >= 0 ? SYS_GREEN : SYS_RED} />
          <R label="Markup" val={trueCost > 0 ? ((profitPU/trueCost)*100).toFixed(1)+'%' : '—'} color={MUTED} />
          {monthlyOverhead > 0 && profitPU > 0 && <R label="Units to cover overhead/mo" val={Math.ceil(monthlyOverhead/profitPU)} color={SYS_BLUE} />}

          {sim.currentPrice > 0 && (
            <div style={{ marginTop:14, paddingTop:14, borderTop:'1px solid #F2F2F7' }}>
              <div style={{ fontSize:11, fontWeight:600, color:MUTED, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>At Current Price ({fmt(sim.currentPrice)})</div>
              <R label="Profit / Unit" val={curProfit !== null ? fmt(curProfit) : '—'} color={curProfit >= 0 ? SYS_GREEN : SYS_RED} bold />
              <R label="Margin" val={curMargin !== null ? pct(curMargin) : '—'} color={curMargin >= 0.15 ? SYS_GREEN : SYS_RED} />
              {curProfit < 0 && <div style={{ marginTop:8, padding:'8px 12px', borderRadius:8, background:'rgba(255,59,48,0.08)', fontSize:12, color:SYS_RED, fontWeight:500 }}>⚠ Selling below true cost by {fmt(Math.abs(curProfit))} per unit</div>}
            </div>
          )}
        </div>

        <div style={{ ...CARD, padding:'16px 18px', fontSize:12, color:MUTED, lineHeight:1.6 }}>
          <b style={{ color:INK }}>Margin vs Markup</b><br/>
          Margin = profit ÷ price → {sim.targetMargin}% means {fmt(profitPU)} of each {fmt(recPrice)} sale is profit.<br/>
          Markup = profit ÷ cost → {trueCost > 0 ? ((profitPU/trueCost)*100).toFixed(1) : 0}% markup on {fmt(trueCost)} cost.
        </div>
      </div>
    </div>
  );
}

// ── Pricing Panel Modal ────────────────────────────────────────────────────────
function PricingPanel({ item, shipment, items, scenario, monthlyOverhead, onSave, onClose }) {
  const landed = shipment ? calcLanded(item, shipment, items) : { landedCost: item.unitCost||0, allocPerUnit:0, breakdown:{} };
  const DEFAULT_FEES = [{ name:'Credit Card', type:'percent', value:2.9 }];

  const [form, setForm] = useState({
    name: 'Default',
    storage: { monthlyCostPerUnit: 0, months: 0 },
    overhead: { expectedMonthlySales: 100 },
    sellingFees: DEFAULT_FEES,
    targetMargin: 0.30,
    currentSellingPrice: 0,
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (scenario) setForm({ ...scenario });
  }, [scenario]);

  function setF(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function setStorage(k, v) { setForm(f => ({ ...f, storage: { ...f.storage, [k]: parseFloat(v)||0 } })); }
  function setOverhead(k, v) { setForm(f => ({ ...f, overhead: { ...f.overhead, [k]: parseFloat(v)||0 } })); }
  function addFee() { setForm(f => ({ ...f, sellingFees: [...f.sellingFees, { name:'New Fee', type:'percent', value:0 }] })); }
  function removeFee(i) { setForm(f => ({ ...f, sellingFees: f.sellingFees.filter((_,idx)=>idx!==i) })); }
  function setFee(i, k, v) { setForm(f => ({ ...f, sellingFees: f.sellingFees.map((fee,idx) => idx===i ? {...fee,[k]:k==='value'?parseFloat(v)||0:v} : fee) })); }

  const pricing = calcPricing(landed, form, monthlyOverhead);

  async function handleSave() {
    setSaving(true);
    try {
      await onSave({ ...form, itemId: item.id, targetMargin: parseFloat(form.targetMargin)||0, currentSellingPrice: parseFloat(form.currentSellingPrice)||0 });
      onClose();
    } catch { /* ignore */ }
    finally { setSaving(false); }
  }

  const R = ({ label, val, color, bold, note }) => (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', padding:'7px 0', borderBottom:'1px solid #F9F9F9' }}>
      <div>
        <span style={{ fontSize:13, color:SECONDARY }}>{label}</span>
        {note && <span style={{ fontSize:10, color:MUTED, marginLeft:6 }}>{note}</span>}
      </div>
      <span style={{ fontSize:bold?15:13, fontWeight:bold?700:500, color:color||INK, fontVariantNumeric:'tabular-nums' }}>{val}</span>
    </div>
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16, fontFamily:FONT }}>
      <div style={{ background:'white', borderRadius:20, width:'100%', maxWidth:900, maxHeight:'94vh', overflow:'auto', boxShadow:'0 24px 64px rgba(0,0,0,0.3)', display:'flex', flexDirection:'column' }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 24px 14px', borderBottom:'1px solid #F2F2F7', flexShrink:0 }}>
          <div>
            <h2 style={{ fontSize:18, fontWeight:700, color:INK, margin:'0 0 2px' }}>{item.productName}</h2>
            <div style={{ fontSize:12, color:MUTED }}>{item.sku ? `SKU: ${item.sku} · ` : ''}Landed: {fmt(landed.landedCost)}/unit</div>
          </div>
          <button onClick={onClose} style={{ background:'#F2F2F7', border:'none', cursor:'pointer', borderRadius:'50%', width:30, height:30, display:'flex', alignItems:'center', justifyContent:'center', color:MUTED }}>
            <X size={15} />
          </button>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', flex:1, overflow:'auto' }}>
          {/* Left: Cost breakdown + inputs */}
          <div style={{ padding:'20px 24px', borderRight:'1px solid #F2F2F7', overflowY:'auto' }}>
            <h4 style={{ fontSize:13, fontWeight:700, color:INK, margin:'0 0 12px', textTransform:'uppercase', letterSpacing:'0.05em' }}>Cost Breakdown</h4>

            {/* Landed cost details */}
            <div style={{ background:'#F9F9FB', borderRadius:12, padding:'14px 16px', marginBottom:16 }}>
              <R label="Supplier Unit Cost" val={fmt(item.unitCost)} />
              {COST_KEYS.filter(k => (landed.breakdown[k]||0) > 0.005).map(k => (
                <R key={k} label={`+ ${COST_LABELS[k]} (allocated)`} val={fmt(landed.breakdown[k])} color={SYS_ORANGE} note={`${(landed.share*100).toFixed(1)}% share`} />
              ))}
              <R label="Landed Cost / Unit" val={fmt(landed.landedCost)} color={SYS_BLUE} bold />
            </div>

            {/* Storage */}
            <h4 style={{ fontSize:12, fontWeight:700, color:MUTED, margin:'0 0 10px', textTransform:'uppercase', letterSpacing:'0.05em' }}>Storage</h4>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
              <div>
                <label style={LBL}>Monthly Cost/Unit ($)</label>
                <input type="number" min="0" step="0.01" value={form.storage.monthlyCostPerUnit} onChange={e => setStorage('monthlyCostPerUnit', e.target.value)} style={INP} />
              </div>
              <div>
                <label style={LBL}>Months in Storage</label>
                <input type="number" min="0" step="1" value={form.storage.months} onChange={e => setStorage('months', e.target.value)} style={INP} />
              </div>
            </div>

            {/* Overhead */}
            <h4 style={{ fontSize:12, fontWeight:700, color:MUTED, margin:'0 0 10px', textTransform:'uppercase', letterSpacing:'0.05em' }}>Overhead</h4>
            <div style={{ background:'rgba(132,204,22,0.07)', borderRadius:10, padding:'10px 14px', marginBottom:10, fontSize:12, color:MUTED }}>
              Auto-loaded from recurring expenses: <b style={{ color:INK }}>{fmt(monthlyOverhead)}/mo</b>
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={LBL}>Expected Monthly Sales (units)</label>
              <input type="number" min="1" step="1" value={form.overhead.expectedMonthlySales} onChange={e => setOverhead('expectedMonthlySales', e.target.value)} style={INP} />
            </div>

            {/* Selling fees */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
              <h4 style={{ fontSize:12, fontWeight:700, color:MUTED, margin:0, textTransform:'uppercase', letterSpacing:'0.05em' }}>Selling Fees</h4>
              <button onClick={addFee} style={{ fontSize:11, color:SYS_BLUE, background:'none', border:'none', cursor:'pointer', fontFamily:FONT, padding:0 }}>+ Add Fee</button>
            </div>
            {form.sellingFees.map((fee, i) => (
              <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr auto 80px auto', gap:6, marginBottom:8, alignItems:'center' }}>
                <input value={fee.name} onChange={e => setFee(i,'name',e.target.value)} style={{ ...INP, padding:'8px 10px', fontSize:12 }} />
                <select value={fee.type} onChange={e => setFee(i,'type',e.target.value)} style={{ ...INP, padding:'8px 10px', fontSize:12, width:'auto' }}>
                  <option value="percent">%</option>
                  <option value="fixed">$</option>
                </select>
                <input type="number" min="0" step="0.1" value={fee.value} onChange={e => setFee(i,'value',e.target.value)} style={{ ...INP, padding:'8px 10px', fontSize:12 }} />
                <button onClick={() => removeFee(i)} style={{ background:'none', border:'none', cursor:'pointer', color:SYS_RED, padding:'4px', display:'flex' }}><X size={12} /></button>
              </div>
            ))}

            {/* Target margin */}
            <div style={{ marginTop:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                <label style={{ ...LBL, margin:0 }}>Target Profit Margin</label>
                <span style={{ fontSize:14, fontWeight:700, color:BRAND }}>{(form.targetMargin*100).toFixed(0)}%</span>
              </div>
              <input type="range" min={5} max={70} step={1} value={form.targetMargin*100} onChange={e => setF('targetMargin', parseFloat(e.target.value)/100)} style={{ width:'100%', accentColor:BRAND, marginBottom:6 }} />
              <div style={{ fontSize:11, color:MUTED }}>Formula: price = {fmt(pricing.trueCost)} ÷ (1 − {(form.targetMargin*100).toFixed(0)}% − {(pricing.pctFees*100).toFixed(1)}%)</div>
            </div>

            <div style={{ marginTop:14 }}>
              <label style={LBL}>Current Selling Price ($)</label>
              <input type="number" min="0" step="0.01" value={form.currentSellingPrice} onChange={e => setF('currentSellingPrice', e.target.value)} style={INP} placeholder="What are you selling it for today?" />
            </div>
          </div>

          {/* Right: Results */}
          <div style={{ padding:'20px 24px', overflowY:'auto', background:'#FAFAFA', borderRadius:'0 20px 20px 0' }}>
            <h4 style={{ fontSize:13, fontWeight:700, color:INK, margin:'0 0 14px', textTransform:'uppercase', letterSpacing:'0.05em' }}>Pricing Summary</h4>

            {/* True cost breakdown */}
            <div style={{ background:'white', borderRadius:14, padding:'16px 18px', marginBottom:14, border:'1px solid #F2F2F7' }}>
              <R label="Landed Cost" val={fmt(landed.landedCost)} color={SYS_BLUE} />
              <R label={`+ Storage (${form.storage.months} mo)`} val={fmt(pricing.storageCost)} />
              <R label={`+ Overhead (${fmt(monthlyOverhead)}/mo ÷ ${form.overhead.expectedMonthlySales} units)`} val={fmt(pricing.ovhPerUnit)} />
              <R label="+ Fixed Fees/unit" val={fmt(pricing.fixedFees)} />
              <R label="True Cost / Unit" val={fmt(pricing.trueCost)} color={INK} bold />
            </div>

            {/* Recommended price */}
            <div style={{ background:BRAND+'12', borderRadius:14, padding:'18px 20px', marginBottom:14 }}>
              <div style={{ fontSize:11, fontWeight:600, color:MUTED, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:4 }}>Recommended Price ({(form.targetMargin*100).toFixed(0)}% margin)</div>
              <div style={{ fontSize:36, fontWeight:800, color:BRAND, fontVariantNumeric:'tabular-nums' }}>{fmt(pricing.recPrice)}</div>
              <div style={{ fontSize:12, color:MUTED, marginTop:4 }}>Profit: {fmt(pricing.profitPU)} per unit · {pct(pricing.actualMargin)} margin · {pricing.markup.toFixed(1)}% markup</div>
            </div>

            <div style={{ background:'white', borderRadius:14, padding:'16px 18px', marginBottom:14, border:'1px solid #F2F2F7' }}>
              <R label="Break-Even Price" val={fmt(pricing.bePrice)} color={SYS_ORANGE} bold />
              <R label="Profit / Unit" val={fmt(pricing.profitPU)} color={pricing.profitPU >= 0 ? SYS_GREEN : SYS_RED} bold />
              <R label="Actual Margin" val={pct(pricing.actualMargin)} color={pricing.actualMargin >= 0.25 ? SYS_GREEN : SYS_ORANGE} />
              <R label="Markup %" val={pricing.markup.toFixed(1)+'%'} color={MUTED} />
              {pricing.unitsOvh && <R label="Units to cover overhead/mo" val={pricing.unitsOvh} color={SYS_BLUE} />}
            </div>

            {/* Storage time analysis */}
            <div style={{ background:'white', borderRadius:14, padding:'16px 18px', marginBottom:14, border:'1px solid #F2F2F7' }}>
              <div style={{ fontSize:12, fontWeight:700, color:MUTED, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:10 }}>Storage Impact</div>
              {[1,3,6,12].map(mo => {
                const sc = form.storage.monthlyCostPerUnit * mo;
                const tc = landed.landedCost + sc + pricing.ovhPerUnit + pricing.fixedFees;
                const rp = (1 - form.targetMargin - pricing.pctFees) > 0.01 ? tc / (1 - form.targetMargin - pricing.pctFees) : tc;
                return (
                  <div key={mo} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid #F9F9F9', fontSize:12 }}>
                    <span style={{ color:SECONDARY }}>{mo} month{mo>1?'s':''}</span>
                    <span style={{ color:MUTED }}>Cost: {fmt(tc)}</span>
                    <span style={{ fontWeight:600, color:BRAND }}>Price: {fmt(rp)}</span>
                  </div>
                );
              })}
            </div>

            {/* Current price analysis */}
            {pricing.curPrice > 0 && (
              <div style={{ background: pricing.curProfit < 0 ? 'rgba(255,59,48,0.06)' : 'rgba(52,199,89,0.06)', borderRadius:14, padding:'14px 18px', marginBottom:14, border:`1px solid ${pricing.curProfit < 0 ? '#FFD0CC' : '#D1FAE5'}` }}>
                <div style={{ fontSize:12, fontWeight:700, color:MUTED, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8 }}>At Your Current Price ({fmt(pricing.curPrice)})</div>
                <R label="Profit/Unit" val={fmt(pricing.curProfit)} color={pricing.curProfit >= 0 ? SYS_GREEN : SYS_RED} bold />
                <R label="Margin" val={pricing.curMargin !== null ? pct(pricing.curMargin) : '—'} color={pricing.curMargin >= 0.15 ? SYS_GREEN : SYS_RED} />
                {pricing.curProfit < 0 && <div style={{ marginTop:8, fontSize:12, color:SYS_RED, fontWeight:600 }}>⚠ You are selling below true cost</div>}
                {pricing.curProfit >= 0 && pricing.curMargin < form.targetMargin && (
                  <div style={{ marginTop:8, fontSize:12, color:SYS_ORANGE, fontWeight:500 }}>Raise price by {fmt(pricing.recPrice - pricing.curPrice)} to hit your {(form.targetMargin*100).toFixed(0)}% target</div>
                )}
              </div>
            )}

            <button onClick={handleSave} disabled={saving} style={{ width:'100%', padding:'13px', background:BRAND, border:'none', borderRadius:12, color:'white', fontWeight:700, fontSize:15, cursor:saving?'not-allowed':'pointer', opacity:saving?0.8:1, fontFamily:FONT }}>
              {saving ? 'Saving…' : scenario ? 'Update Pricing' : 'Save Pricing'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
